/**
 * TanStack Query hooks for the AI Studio admin surface (#19).
 *
 * Mirrors `api/src/ai/studio/studio.routes.ts`:
 *
 *   - POST   /api/admin/ai/generate                 → useGenerate
 *   - POST   /api/admin/ai/generations/:id/accept   → useAcceptGeneration
 *   - POST   /api/admin/ai/generations/:id/reject   → useRejectGeneration
 *   - GET    /api/admin/ai/usage                    → useAiUsage
 *   - GET    /api/admin/ai/budget-status            → useAiBudgetStatus
 *   - POST   /api/admin/ai/override-budget          → useStartBudgetOverride
 *
 * Mutations invalidate `queryKeys.ai.all` so the budget banner + usage
 * dashboard re-fetch immediately after a state-changing call. The
 * budget-status query polls every 30s on the `/ai/*` pages so a 429
 * surfaced by the gating middleware shows up in the banner without the
 * operator having to refresh the page.
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type {
  AiBudgetOverrideInput,
  AiBudgetOverrideResponse,
  AiBudgetStatusResponse,
  AiGenerateBrandBriefInput,
  AiGenerateBrandBriefResponse,
  AiGenerateLayoutInput,
  AiGenerateLayoutResponse,
  AiGenerateThemeInput,
  AiGenerateThemeResponse,
  AiStudioAcceptResponse,
  AiStudioGenerateInput,
  AiStudioGenerateResponse,
  AiStudioRejectResponse,
  AiUsageResponse,
} from "@ottopilot/shared";
import { apiGet, apiPost } from "../lib/api";
import type { ApiClientError } from "../lib/api";
import { queryKeys } from "./queryKeys";

/** 30-second cadence — matches the API's in-process usage cache TTL. */
const BUDGET_STATUS_POLL_MS = 30_000;

/**
 * Fire a generate call. The mutation does NOT invalidate the AI cache
 * itself — the SPA holds the result locally until the operator
 * accepts / rejects, then the accept/reject mutations invalidate.
 */
export function useGenerate() {
  return useMutation<AiStudioGenerateResponse, ApiClientError, AiStudioGenerateInput>({
    mutationFn: (input) => apiPost<AiStudioGenerateResponse>("/api/admin/ai/generate", input),
  });
}

export function useAcceptGeneration() {
  const qc = useQueryClient();
  return useMutation<AiStudioAcceptResponse, ApiClientError, { generationId: string }>({
    mutationFn: ({ generationId }) =>
      apiPost<AiStudioAcceptResponse>(`/api/admin/ai/generations/${generationId}/accept`),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.ai.all });
    },
  });
}

export function useRejectGeneration() {
  const qc = useQueryClient();
  return useMutation<AiStudioRejectResponse, ApiClientError, { generationId: string }>({
    mutationFn: ({ generationId }) =>
      apiPost<AiStudioRejectResponse>(`/api/admin/ai/generations/${generationId}/reject`),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.ai.all });
    },
  });
}

export function useAiUsage(month: string | undefined) {
  return useQuery<AiUsageResponse, ApiClientError>({
    queryKey: queryKeys.ai.usage(month),
    queryFn: () =>
      apiGet<AiUsageResponse>("/api/admin/ai/usage", {
        query: { month },
      }),
    staleTime: 30_000,
  });
}

/**
 * Polls every 30s. We use TanStack Query's `refetchInterval` rather than
 * a custom `setInterval` so the polling pauses automatically when the
 * tab is hidden (the default `refetchIntervalInBackground: false`).
 *
 * On a 429 AI_BUDGET_EXCEEDED the API throws an `ApiClientError`; the
 * banner reads `error.code` to render its message instead of treating
 * the 429 as a generic failure.
 */
export function useAiBudgetStatus() {
  return useQuery<AiBudgetStatusResponse, ApiClientError>({
    queryKey: queryKeys.ai.budgetStatus,
    queryFn: () => apiGet<AiBudgetStatusResponse>("/api/admin/ai/budget-status"),
    refetchInterval: BUDGET_STATUS_POLL_MS,
    // Don't infinitely retry a 429 — the override flow is the operator's
    // path forward, not a rapid retry loop.
    retry: false,
  });
}

export function useStartBudgetOverride() {
  const qc = useQueryClient();
  return useMutation<AiBudgetOverrideResponse, ApiClientError, AiBudgetOverrideInput>({
    mutationFn: (input) =>
      apiPost<AiBudgetOverrideResponse>("/api/admin/ai/override-budget", input),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.ai.all });
    },
  });
}

/**
 * `POST /api/admin/pages/:pageId/versions/:versionId/ai-generate-layout`
 * — generate a full page layout from a natural-language prompt (#181).
 *
 * The mutation invalidates both the AI cache (so the budget banner picks
 * up the new spend on its next poll) and the targeted page's detail
 * query (so the editor re-renders with the freshly-created
 * blocksOrderJson + ContentItems). The hook takes `pageId` at
 * construction time so the success invalidation targets the right
 * detail key without the caller threading it through `variables`.
 */
export function useGenerateLayout(pageId: string) {
  const qc = useQueryClient();
  return useMutation<
    AiGenerateLayoutResponse,
    ApiClientError,
    { versionId: string; input: AiGenerateLayoutInput }
  >({
    mutationFn: ({ versionId, input }) =>
      apiPost<AiGenerateLayoutResponse>(
        `/api/admin/pages/${pageId}/versions/${versionId}/ai-generate-layout`,
        input,
      ),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.ai.all });
      void qc.invalidateQueries({ queryKey: queryKeys.pages.detail(pageId) });
      // ContentItems on the site grew — invalidate that browser too so
      // the operator sees the new rows without a manual refresh.
      void qc.invalidateQueries({ queryKey: queryKeys.contentItems.all });
    },
  });
}

/**
 * `POST /api/admin/sites/:siteId/ai/theme` — generate a `themeJsonV1`
 * proposal from a natural-language brief (#192).
 *
 * The endpoint is read-only: it does NOT write to `Site.themeJson`. The
 * mutation invalidates the AI cache (so the budget banner picks up the
 * new spend on its next poll) but NOT the site detail — the operator
 * still has to click "Save theme" in the ThemeTab editor to persist.
 *
 * The hook takes `siteId` at construction time so the success
 * invalidation key is correct without the caller threading it through
 * `variables`.
 */
export function useGenerateTheme(siteId: string) {
  const qc = useQueryClient();
  return useMutation<AiGenerateThemeResponse, ApiClientError, AiGenerateThemeInput>({
    mutationFn: (input) =>
      apiPost<AiGenerateThemeResponse>(`/api/admin/sites/${siteId}/ai/theme`, input),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.ai.all });
    },
  });
}

/**
 * `POST /api/admin/sites/:siteId/ai/brand-brief` — generate a `brandJsonV1`
 * proposal from a natural-language brand pitch (#230).
 *
 * The endpoint is read-only: it does NOT write to `Site.brandJson`. The
 * mutation invalidates the AI cache (so the budget banner picks up the
 * new spend on its next poll) but NOT the site detail — the operator
 * still has to click "Save brand brief" in the BrandBriefSection editor to
 * persist.
 *
 * The hook takes `siteId` at construction time so the success
 * invalidation key is correct without the caller threading it through
 * `variables`. Mirrors `useGenerateTheme` (#192).
 */
export function useGenerateBrandBrief(siteId: string) {
  const qc = useQueryClient();
  return useMutation<AiGenerateBrandBriefResponse, ApiClientError, AiGenerateBrandBriefInput>({
    mutationFn: (input) =>
      apiPost<AiGenerateBrandBriefResponse>(`/api/admin/sites/${siteId}/ai/brand-brief`, input),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.ai.all });
    },
  });
}
